import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { CAMBIOS_MERCADO_ENDPOINTS } from '../../shared/models/Constants';
import { ICustomResponseResult } from '../../shared/models/request.model';
import { SharedServiceService } from '../../shared/services/shared-service.service';
import {
  cancelTicket,
  createTicket,
  errorCancelTicket,
  errorCreateTicket,
  errorGetCPCatalogo,
  errorGetCPReasons,
  errorGetTicketActivo,
  errorUpdateTicket,
  getCPCatalogo,
  getCPReasons,
  getTicketActivo,
  successCancelTicket,
  successCreateTicket,
  successGetCPCatalogo,
  successGetCPReasons,
  successGetTicketActivo,
  successUpdateTicket,
  updateTicket,
} from '../actions/cambios-mercado.actions';

@Injectable()
export class CambiosMercadoEffects {
    constructor(
        private actions$: Actions,
        private httpS: SharedServiceService
    ) { }

    /**
     * * Consultar catálogo de productos
     */
    public getCPCatalogo$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getCPCatalogo),
            switchMap(() =>
                this.httpS.executeRequestByUser(CAMBIOS_MERCADO_ENDPOINTS.get_catalogo, null)
                    .pipe(
                        map((response: ICustomResponseResult<any>) =>
                            successGetCPCatalogo(response.data)
                        ),
                        catchError((err: Error) =>
                            of(errorGetCPCatalogo({ payload: err }))
                        )
                    )
            )
        )
    );

    /**
     * * Consultar catalogo de motivos por SKU
     */
    public getCPReasons$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getCPReasons),
            switchMap(params =>
                this.httpS.executeRequestByUser(CAMBIOS_MERCADO_ENDPOINTS.get_reasons, params)
                    .pipe(
                        map((response: ICustomResponseResult<any>) =>
                            successGetCPReasons(response.data)
                        ),
                        catchError((err: Error) =>
                            of(errorGetCPReasons({ payload: err }))
                        )
                    )
            )
        )
    );

    /**
     * * Crear ticket de cambio de mercado
     */
    public createTicket$ = createEffect(() =>
        this.actions$.pipe(
            ofType(createTicket),
            switchMap((request) =>
                this.httpS.executeRequestByUser(
                    CAMBIOS_MERCADO_ENDPOINTS.create_ticket,
                    { ...request, idempotency_key: this.generateNonce() }
                )
                    .pipe(
                        map((response: ICustomResponseResult<any>) =>
                            successCreateTicket(response.data)
                        ),
                        catchError((err: Error) =>
                            of(errorCreateTicket({ payload: err }))
                        )
                    )
            )
        )
    );

    /**
     * * Cancelar ticket de cambio de mercado
     */
    public cancelTicket$ = createEffect(() =>
        this.actions$.pipe(
            ofType(cancelTicket),
            switchMap((request) =>
                this.httpS.executeRequestByUser(
                    { ...CAMBIOS_MERCADO_ENDPOINTS.cancel_ticket, url: `${CAMBIOS_MERCADO_ENDPOINTS.cancel_ticket.url}/${request.folio}` },
                    { ...request, idempotency_key: this.generateNonce() }
                )
                    .pipe(
                        map((response: ICustomResponseResult<any>) =>
                            successCancelTicket(response.data)
                        ),
                        catchError((err: Error) =>
                            of(errorCancelTicket({ payload: err }))
                        )
                    )
            )
        )
    );

    /**
     * * Actualizar ticket de cambio de mercado
     */
    public updateTicket$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateTicket),
            switchMap((request) =>
                this.httpS.executeRequestByUser(
                    { ...CAMBIOS_MERCADO_ENDPOINTS.update_ticket, url: `${CAMBIOS_MERCADO_ENDPOINTS.update_ticket.url}/${request.id}` },
                    { ...request, idempotency_key: this.generateNonce() }
                )
                    .pipe(
                        map((response: ICustomResponseResult<any>) =>
                            successUpdateTicket(response.data)
                        ),
                        catchError((err: Error) =>
                            of(errorUpdateTicket({ payload: err }))
                        )
                    )
            )
        )
    );

    /**
     * * Consultar tickets activos
     */
    public getTicketActivo$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getTicketActivo),
            switchMap(() =>
                this.httpS.executeRequestByUser(CAMBIOS_MERCADO_ENDPOINTS.get_ticket_activo, null)
                    .pipe(
                        map(resp => successGetTicketActivo(resp)),
                        catchError((err: Error) => of(errorGetTicketActivo({ payload: err })))
                    )
            )
        )
    );

    private generateNonce = (): string => {
        const u = Date.now().toString(16) + Math.random().toString(16) + '0'.repeat(16);
        return [u.substring(0, 8), u.substring(8, 12), '4000-8' + u.substring(13, 16), u.substring(16, 28)].join('-');
    }
}
